import { Component, DestroyRef, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { finalize, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { GroupService } from '../../services/group-service/group.service';
import { CustomerService } from '../../services/customer-service/customer.service';
import { resolveApiErrorMessage } from '../../services/api-error-message.util';
import {
  getGroupNameErrorMessage,
  getGroupNameValidators,
  GROUP_NAME_HINT,
  GROUP_NAME_MAX_LENGTH,
} from '../../services/group-name.validators';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import {
  bindFormDirtyTracking,
  clearFormUnsavedChanges,
} from '../../services/unsaved-changes/unsaved-changes-form.util';
import {
  ConfirmMoveGroupCustomerComponent,
  ConfirmMoveGroupCustomerData,
} from '../confirm-move-group-customer/confirm-move-group-customer.component';

const GROUP_FORM_DIRTY_SOURCE = 'group-form-modal';

interface ApiResponse {
  success?: boolean;
  message?: string;
  data?: {
    id?: number | string;
    groupId?: number | string;
    groupName?: string;
    isCommon?: boolean;
  };
}

export interface GroupDialogData {
  id?: number | string;
  groupName?: string;
  groupType?: 'COMMON' | 'CUSTOMER_SPECIFIC';
  customerId?: number | string;
  customerName?: string;
  deviceCount?: number;
}

@Component({
  selector: 'app-group-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatDialogModule,
    MatRadioModule,
    MatInputModule,
  ],
  templateUrl: './group-dialog.component.html',
  styleUrl: './group-dialog.component.scss',
})
export class GroupDialogComponent implements OnInit, OnDestroy {
  private static readonly MONTAGE_CUSTOMER_NAME = 'Montage';

  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedChanges = inject(UnsavedChangesTrackerService);

  groupForm!: FormGroup;
  isSubmitting = false;
  isEditMode = false;
  customersList: any[] = [];
  groupNameServerError = '';
  readonly groupNameHint = GROUP_NAME_HINT;
  readonly groupNameMaxLength = GROUP_NAME_MAX_LENGTH;

  readonly GROUP_TYPE_COMMON = 'COMMON';
  readonly GROUP_TYPE_CUSTOMER_SPECIFIC = 'CUSTOMER_SPECIFIC';

  /** Cached Montage customer record id for Public group create. */
  private montageCustomerId: number | string | null = null;
  private montageCustomerName = GroupDialogComponent.MONTAGE_CUSTOMER_NAME;

  private readonly customerSearchPayload = {
    page: 0,
    size: 100,
    filters: [{ field: '', operator: '', value: '' }],
    sorts: [{ field: 'customerName', direction: 'asc' }],
  };

  constructor(
    private dialogRef: MatDialogRef<GroupDialogComponent>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private groupService: GroupService,
    private customerService: CustomerService,
    @Inject(MAT_DIALOG_DATA) public data: GroupDialogData | null
  ) {
    this.isEditMode = !!(data && data.id);
  }

  ngOnInit(): void {
    this.initForm();
    bindFormDirtyTracking(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges,
      this.destroyRef
    );
    this.loadCustomers();

    if (this.isEditMode && this.data) {
      this.prefillForm(this.data);
    }

    this.groupForm.get('groupType')?.valueChanges.subscribe((value: string) => {
      this.onGroupTypeChange(value);
    });

    this.groupForm.get('groupName')?.valueChanges.subscribe(() => {
      if (this.groupNameServerError) {
        this.groupNameServerError = '';
      }
    });

    clearFormUnsavedChanges(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }

  ngOnDestroy(): void {
    this.unsavedChanges.clear(GROUP_FORM_DIRTY_SOURCE);
  }

  private initForm(): void {
    this.groupForm = this.fb.group({
      groupName: ['', getGroupNameValidators()],
      groupType: [this.GROUP_TYPE_COMMON, Validators.required],
      customerId: [{ value: '', disabled: true }],
    });
  }

  private prefillForm(data: GroupDialogData): void {
    const groupType = data.groupType ?? this.GROUP_TYPE_COMMON;
    const isCustomerSpecific = groupType === this.GROUP_TYPE_CUSTOMER_SPECIFIC;

    this.groupForm.patchValue({
      groupName: data.groupName ?? '',
      groupType,
      customerId: data.customerId ?? '',
    });

    if (isCustomerSpecific) {
      this.groupForm.get('customerId')?.enable();
    }

    clearFormUnsavedChanges(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
  }

  private onGroupTypeChange(value: string): void {
    const customerControl = this.groupForm.get('customerId');
    if (value === this.GROUP_TYPE_CUSTOMER_SPECIFIC) {
      customerControl?.enable();
    } else {
      customerControl?.disable();
      customerControl?.setValue('');
    }
  }

  private loadCustomers(): void {
    this.customerService.getAllCustomer(this.customerSearchPayload).subscribe({
      next: (res: any) => {
        this.customersList = res?.data?.content ?? [];
        this.cacheMontageCustomerId(this.customersList);
        if (this.montageCustomerId == null) {
          this.loadMontageCustomerId();
        }
      },
      error: () => {
        this.customersList = [];
        this.loadMontageCustomerId();
      },
    });
  }

  /** Resolve Montage from a customer list (name match, same pattern as customer dialog). */
  private findMontageCustomer(
    list: Array<{ id?: number | string; customerName?: string }>
  ): { id?: number | string; customerName?: string } | undefined {
    const target = GroupDialogComponent.MONTAGE_CUSTOMER_NAME.toLowerCase();
    return list.find((row) => {
      const name = String(row.customerName ?? '')
        .trim()
        .toLowerCase();
      return (
        name === target ||
        name.startsWith(`${target} `) ||
        name.startsWith(`${target}(`)
      );
    });
  }

  private cacheMontageCustomerId(
    list: Array<{ id?: number | string; customerId?: number | string; customerName?: string }>
  ): void {
    const montage = this.findMontageCustomer(list);
    if (!montage) {
      return;
    }
    const id = this.resolveCustomerRecordId(montage);
    if (id != null) {
      this.montageCustomerId = id;
    }
    const name = String(montage.customerName ?? '').trim();
    if (name) {
      this.montageCustomerName = name;
    }
  }

  /** Prefer internal record `id` (same value Private dropdown uses). */
  private resolveCustomerRecordId(row: {
    id?: number | string;
    customerId?: number | string;
  }): number | null {
    for (const raw of [row.id, row.customerId]) {
      if (raw == null || raw === '') {
        continue;
      }
      const numeric = Number(raw);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }
    return null;
  }

  /** Targeted lookup when Montage is not on the first customer page. */
  private loadMontageCustomerId(): void {
    this.customerService
      .getAllCustomer({
        page: 0,
        size: 20,
        filters: [
          {
            field: 'customerName',
            operator: 'like',
            value: GroupDialogComponent.MONTAGE_CUSTOMER_NAME,
          },
        ],
        sorts: [{ field: 'customerName', direction: 'asc' }],
      })
      .subscribe({
        next: (res: any) => {
          this.cacheMontageCustomerId(res?.data?.content ?? []);
        },
        error: () => {
          // Leave montageCustomerId null; create will surface a clear error.
        },
      });
  }

  private resolveMontageCustomerId(): number | string | null {
    if (this.montageCustomerId != null) {
      return this.montageCustomerId;
    }
    this.cacheMontageCustomerId(this.customersList);
    return this.montageCustomerId;
  }

  get isCustomerSpecific(): boolean {
    return this.groupForm.get('groupType')?.value === this.GROUP_TYPE_CUSTOMER_SPECIFIC;
  }

  get groupNameErrorMessage(): string {
    if (this.groupNameServerError) {
      return this.groupNameServerError;
    }
    const control = this.groupForm.get('groupName');
    if (!control || !(control.touched || control.dirty) || !control.invalid) {
      return '';
    }
    return getGroupNameErrorMessage(control.errors);
  }

  onSubmit(): void {
    this.groupNameServerError = '';
    this.groupForm.markAllAsTouched();

    if (this.groupForm.invalid || this.isSubmitting) {
      return;
    }

    const payload = this.buildSubmitPayload();
    if (!payload) {
      return;
    }

    if (this.isEditMode && this.data?.id) {
      this.submitGroupUpdate(payload);
      return;
    }

    this.submitGroupCreate(payload);
  }

  private buildSubmitPayload(): {
    groupName: string;
    isCommon: boolean;
    customerId?: number | string;
  } | null {
    const raw = this.groupForm.getRawValue();
    const isCommon = raw.groupType === this.GROUP_TYPE_COMMON;
    const groupName = String(raw.groupName ?? '').trim();

    this.groupForm.get('groupName')?.setValue(groupName, { emitEvent: false });
    if (this.groupForm.get('groupName')?.invalid) {
      return null;
    }

    const payload: { groupName: string; isCommon: boolean; customerId?: number | string } = {
      groupName,
      isCommon,
    };

    if (isCommon) {
      return this.attachMontageCustomerForPublicCreate(payload);
    }

    if (raw.customerId) {
      payload.customerId = Number(raw.customerId) || raw.customerId;
    }
    return payload;
  }

  /** Public groups are associated with Montage on create only. */
  private attachMontageCustomerForPublicCreate(payload: {
    groupName: string;
    isCommon: boolean;
    customerId?: number | string;
  }): { groupName: string; isCommon: boolean; customerId?: number | string } | null {
    if (this.isEditMode) {
      return payload;
    }

    const montageId = this.resolveMontageCustomerId();
    if (montageId == null) {
      this.toastr.error(
        'Unable to determine Montage customer. Please refresh and try again.',
        'Create Failed',
        { timeOut: 8000, closeButton: true, progressBar: true }
      );
      return null;
    }

    payload.customerId = Number(montageId);
    return payload;
  }

  private submitGroupUpdate(payload: {
    groupName: string;
    isCommon: boolean;
    customerId?: number | string;
  }): void {
    if (this.isConvertingPrivateToPublicWithDevices(payload.isCommon)) {
      this.warnPrivateToPublicBlocked();
      return;
    }

    this.confirmCustomerMoveIfNeeded(payload)
      .pipe(
        switchMap((confirmed) => {
          if (!confirmed) {
            return of(null);
          }
          this.isSubmitting = true;
          return this.groupService.updateGroup(this.data!.id!, payload).pipe(
            finalize(() => (this.isSubmitting = false))
          );
        })
      )
      .subscribe({
        next: (response: ApiResponse | null) => {
          if (!response) {
            return;
          }
          this.onUpdateSuccess(response, payload);
        },
        error: (error: unknown) => {
          this.handleSubmitError(
            error,
            'Update Failed',
            'Failed to update group. Please check the group name and try again.'
          );
        },
      });
  }

  private warnPrivateToPublicBlocked(): void {
    const count = Number(this.data?.deviceCount);
    const deviceLabel = count === 1 ? 'device' : 'devices';
    this.toastr.warning(
      `Cannot convert this group to Public while it has ${count} associated ${deviceLabel}. Reassign the devices to another group first.`,
      'Update Blocked',
      { timeOut: 8000, closeButton: true, progressBar: true }
    );
  }

  private onUpdateSuccess(
    response: ApiResponse,
    payload: { groupName: string; isCommon: boolean; customerId?: number | string }
  ): void {
    this.toastr.success(
      response?.message ?? this.buildGroupUpdatedMessage(payload),
      'Group Updated',
      { timeOut: 30000, closeButton: true, progressBar: true }
    );
    clearFormUnsavedChanges(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
    this.dialogRef.close({ success: true });
  }

  private buildGroupUpdatedMessage(payload: {
    isCommon: boolean;
    customerId?: number | string;
  }): string {
    const deviceCount = Number(this.data?.deviceCount);
    const movedDevices =
      this.isCustomerChange(payload) &&
      Number.isFinite(deviceCount) &&
      deviceCount > 0;

    if (!movedDevices) {
      return 'Group has been updated successfully.';
    }

    const deviceLabel = deviceCount === 1 ? 'device' : 'devices';
    return `Group updated. ${deviceCount} associated ${deviceLabel} moved to the new customer.`;
  }

  private submitGroupCreate(payload: {
    groupName: string;
    isCommon: boolean;
    customerId?: number | string;
  }): void {
    this.isSubmitting = true;
    this.groupService.createGroup(payload).subscribe({
      next: (response: ApiResponse) => {
        if (payload.isCommon) {
          this.finalizePublicGroupCreate(response, payload.customerId);
          return;
        }
        this.isSubmitting = false;
        this.onCreateSuccess(response);
      },
      error: (error: unknown) => {
        this.isSubmitting = false;
        this.handleSubmitError(
          error,
          'Create Failed',
          'Failed to create group. Please check the group name and try again.'
        );
      },
    });
  }

  /**
   * BE create for Public (`isCommon`) often omits customer on the group entity.
   * Ensure Montage association via customer↔group assignment so it persists and lists correctly.
   */
  private finalizePublicGroupCreate(
    response: ApiResponse,
    montageCustomerId: number | string | undefined
  ): void {
    const groupId = this.extractCreatedGroupId(response);
    const customerId = montageCustomerId ?? this.resolveMontageCustomerId();

    if (groupId == null || customerId == null) {
      this.isSubmitting = false;
      this.onCreateSuccess(response);
      return;
    }

    this.assignGroupToCustomer(customerId, groupId)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => this.onCreateSuccess(response),
        error: () => {
          this.toastr.warning(
            'Group created, but assigning it to Montage failed. Assign it from Group Assignment if needed.',
            'Partial Success',
            { timeOut: 8000, closeButton: true, progressBar: true }
          );
          this.onCreateSuccess(response);
        },
      });
  }

  private onCreateSuccess(response: ApiResponse): void {
    this.toastr.success(
      response?.message ?? 'Group has been created successfully.',
      'Group Created',
      { timeOut: 30000, closeButton: true, progressBar: true }
    );
    clearFormUnsavedChanges(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
    this.dialogRef.close({ success: true });
  }

  private extractCreatedGroupId(response: ApiResponse | null | undefined): number | string | null {
    const data =
      response?.data ??
      (response as { id?: number | string; groupId?: number | string } | null | undefined);
    const raw = data?.id ?? data?.groupId;
    if (raw == null || raw === '') {
      return null;
    }
    const numeric = Number(raw);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : raw;
  }

  private assignGroupToCustomer(
    customerId: number | string,
    groupId: number | string
  ): Observable<unknown> {
    return this.groupService.getGroupsForAssignment(customerId).pipe(
      switchMap((res: any) => {
        const assigned = this.extractAssignedGroupAssignments(res);
        const alreadyAssigned = assigned.some(
          (entry) => String(entry.groupId) === String(groupId)
        );
        const assignments = alreadyAssigned
          ? assigned
          : [
              ...assigned,
              { groupId, wiliotGatewayFormatEnabled: false },
            ];
        return this.groupService.assignGroupsToCustomer(customerId, assignments);
      })
    );
  }

  private extractAssignedGroupAssignments(
    res: any
  ): { groupId: number | string; wiliotGatewayFormatEnabled: boolean }[] {
    const payload = res?.data ?? res;

    const toAssignment = (g: any) => ({
      groupId: g.id ?? g.groupId,
      wiliotGatewayFormatEnabled: g.wiliotGatewayFormatEnabled ?? false,
    });

    if (payload?.assignedGroups) {
      return (payload.assignedGroups as any[])
        .map(toAssignment)
        .filter((a) => a.groupId != null && a.groupId !== '');
    }

    if (Array.isArray(payload)) {
      const hasAssignedFlag = payload.some(
        (g: any) => g.assigned === true || g.assigned === false
      );
      const assigned = hasAssignedFlag
        ? payload.filter((g: any) => g.assigned === true)
        : payload.filter((g: any) => g.customerGroupId != null);
      return assigned
        .map(toAssignment)
        .filter((a) => a.groupId != null && a.groupId !== '');
    }

    return [];
  }

  private isCustomerChange(payload: {
    isCommon: boolean;
    customerId?: number | string;
  }): boolean {
    if (!this.isEditMode || !this.data || payload.isCommon) {
      return false;
    }
    const original = this.data.customerId;
    const next = payload.customerId;
    if (original == null || next == null || next === '') {
      return false;
    }
    return String(original) !== String(next);
  }

  private isConvertingPrivateToPublicWithDevices(isCommon: boolean): boolean {
    if (!this.isEditMode || !this.data || !isCommon) {
      return false;
    }
    const wasPrivate = this.data.groupType === this.GROUP_TYPE_CUSTOMER_SPECIFIC;
    const deviceCount = Number(this.data.deviceCount);
    return wasPrivate && Number.isFinite(deviceCount) && deviceCount > 0;
  }

  /**
   * When editing a private group that has devices and the customer changes,
   * confirm that associated devices will move with the group.
   */
  private confirmCustomerMoveIfNeeded(payload: {
    groupName: string;
    isCommon: boolean;
    customerId?: number | string;
  }): Observable<boolean> {
    const deviceCount = Number(this.data?.deviceCount);
    if (
      !this.isCustomerChange(payload) ||
      !Number.isFinite(deviceCount) ||
      deviceCount <= 0
    ) {
      return of(true);
    }

    const confirmData: ConfirmMoveGroupCustomerData = {
      groupName: payload.groupName || this.data?.groupName || 'N/A',
      deviceCount,
      currentCustomerName: this.resolveCustomerDisplayName(
        this.data?.customerId,
        this.data?.customerName
      ),
      newCustomerName: this.resolveCustomerDisplayName(payload.customerId),
    };

    const confirmRef = this.dialog.open(ConfirmMoveGroupCustomerComponent, {
      width: '540px',
      restoreFocus: false,
      disableClose: true,
      data: confirmData,
    });

    return confirmRef.afterClosed().pipe(
      switchMap((confirmed: boolean | undefined) => of(!!confirmed))
    );
  }

  private resolveCustomerDisplayName(
    customerId?: number | string | null,
    fallbackName?: string
  ): string {
    if (fallbackName && String(fallbackName).trim()) {
      return String(fallbackName).trim();
    }
    if (customerId == null || customerId === '') {
      return 'N/A';
    }
    const match = this.customersList.find(
      (c) => String(c.id) === String(customerId)
    );
    return match?.customerName ? String(match.customerName) : String(customerId);
  }

  private handleSubmitError(
    error: unknown,
    title: string,
    fallback: string
  ): void {
    const msg = resolveApiErrorMessage(error, { fallback });
    this.groupNameServerError = msg;
    this.toastr.error(msg, title, {
      timeOut: 8000,
      closeButton: true,
      progressBar: true,
    });
  }

  onCancel(): void {
    clearFormUnsavedChanges(
      this.groupForm,
      GROUP_FORM_DIRTY_SOURCE,
      this.unsavedChanges
    );
    this.dialogRef.close();
  }
}
